import * as THREE from "three";
import Light from "@/models/Light";

export default function(x, y, z, scene, camera, vueBus, textureLoader) {
  /*
   * @function - Leg
   * Creates leg element to add to hero
   * @param - material (ThreeJS Material Instance): Material to use
   */
  function Leg(material) {
    Object.setPrototypeOf(this, new THREE.Object3D());

    const geometry = new THREE.CylinderBufferGeometry(0.0833, 0.0833, 0.29167);
    const mesh = new THREE.Mesh(geometry, material);

    this.add(mesh);
  }

  /*
   * @function - Arm
   * Creates arm element to add to hero
   * @param - materialGold (ThreeJS Material Instance): Material to use
   * @param - materialWater (ThreeJS Material Instance): Material to use
   */
  function Arm({ materialGold, materialWater }) {
    Object.setPrototypeOf(this, new THREE.Object3D());

    const geometryArm = new THREE.CylinderBufferGeometry(0.05, 0.05, 0.25);
    const arm = new THREE.Mesh(geometryArm, materialGold);
    arm.position.y = 0.0167;

    const geometryShoulder = new THREE.IcosahedronBufferGeometry(0.05);
    const shoulder = new THREE.Mesh(geometryShoulder, materialWater);
    shoulder.position.y = 0.2083;

    this.add(arm);
    this.add(shoulder);
  }

  /*
   * @function - Head
   * Creates arm element to add to hero
   * @param - x2 (Number): X position of arm
   * @param - y2 (Number): Y position of arm
   * @param - z2 (Number): Z position of arm
   * @param - material (ThreeJS Material Instance): Material to use
   */
  function Head(x2, y2, z2, material) {
    Object.setPrototypeOf(this, new THREE.Object3D());
    this.position.set(x2, y2, x2);
    this.rotation.y = Math.PI / 2;

    const geometry = new THREE.SphereBufferGeometry(0.1875, 8, 8);
    const mesh = new THREE.Mesh(geometry, material);

    this.add(mesh);
  }

  /*
   * @function - Body
   * Creates body element to add to hero
   * @param - material (ThreeJS Material Instance): Material to use
   */
  function Body(material) {
    Object.setPrototypeOf(this, new THREE.Object3D());
    this.rotation.y = Math.PI / 1.6;

    const geometry = new THREE.CylinderBufferGeometry(0.2167, 0.1667, 0.4667);
    const mesh = new THREE.Mesh(geometry, material);

    this.add(mesh);
  }

  /*
   * @function - Box
   * Creates box helper element to add to hero
   * @param - hero (ThreeJS Material Instance): Hero object
   */
  function Box(hero) {
    Object.setPrototypeOf(this, new THREE.BoxHelper(hero, 0xff0000));

    this.visible = false;
    this.name = "Hero";
  }

  Object.setPrototypeOf(this, new THREE.Object3D());
  this.position.set(x, y, z);

  const textureGold = textureLoader.load("/img/3DTextures/xterminate/game/gold.jpg");
  const textureWater = textureLoader.load("/img/3DTextures/xterminate/game/water.jpg");
  const textureMetalhead = textureLoader.load("/img/3DTextures/xterminate/game/metalhead.jpg");
  const textureMetalbody = textureLoader.load("/img/3DTextures/xterminate/game/metalbody.jpg");

  const materialGold = new THREE.MeshPhongMaterial({ map: textureGold });
  const materialWater = new THREE.MeshPhongMaterial({ map: textureWater });
  const materialMetalhead = new THREE.MeshPhongMaterial({ map: textureMetalhead });
  const materialMetalbody = new THREE.MeshPhongMaterial({ map: textureMetalbody });

  const light1 = new Light(0, 0.5, 1, 0xffdddd);
  const light2 = new Light(0, 0.5, -1, 0xffdddd);
  const body = new Body(materialMetalbody);
  const head = new Head(0, 0.4667, 0, materialMetalhead);
  const legL = new Leg(materialGold);
  legL.position.set(-0.1, -0.4333, 0);
  const legR = new Leg(materialGold);
  legR.position.set(0.1, -0.4333, 0);
  const armL = new Arm({ materialGold, materialWater });
  armL.position.set(-0.2667, 0, 0);
  const armR = new Arm({ materialGold, materialWater });
  armR.position.set(0.2667, 0, 0);
  const box = new Box(this);

  this.add(light1);
  this.add(light2);
  this.add(body);
  this.add(head);
  this.add(legL);
  this.add(legR);
  this.add(armL);
  this.add(armR);

  scene.add(box);

  // First camera position
  camera.position.set(this.position.x, 5, this.position.z);

  // Keyboard arrows management
  const minDistance = 0.375;
  const vector = new THREE.Vector3(0, 0, 0);
  const raycasterHero = new THREE.Raycaster(this.position, vector, 0, minDistance);
  const raycasterCam = new THREE.Raycaster(this.position, vector, 0, 4);
  const keyEvents = [];
  const velocity = 0.1;
  const rotBody = 0.13;
  let rotMembers = 0.04;
  let attackVal = 0.02;
  let isDestroyed = false;
  let numWeapon = 0;
  let crashF = 0;
  let crashB = 0;
  let crashL = 0;
  let crashR = 0;
  let objectBackCam = null;
  let objectFrontCam = null;

  /*
   * @function - keyboardEvent
   * Sets key events to move hero
   * @param - e: keyboard event argument
   */
  function keyboardEvent(e) {
    keyEvents[e.keyCode] = e.type === "keydown";
  }

  /*
   * @function - killed
   * Kills hero
   * @param - num (Boolean): true or false
   */
  function killed(num) {
    isDestroyed = num;
  }

  /*
   * @function - damaged
   * Damages hero's stamina
   * @param - num (Number): Value to damage hero
   */
  function damaged(num) {
    attackVal = num;

    if (numWeapon === 0) {
      const villain = scene.children.filter(el => el.name === "Villain");

      for (let i = 0; i < villain.length; i++) {
        villain[i].damaged(attackVal);
      }
    }

    vueBus.$emit("Xterminate: hero damaged", (1 - attackVal) * 50);

    if (attackVal === 1) {
      vueBus.$emit("Xterminate: game finished", 1);
      this.killed(true);
    }
  }

  /*
   * @function - charge
   * Adds weapon in use
   * @param - num (Number): Value of weapons founded
   */
  function charge(num) {
    numWeapon += num;
    // document.write("<embed src='music/THEOBSERVER.wav' hidden=true autostart=false>");

    const villain = scene.children.filter(el => el.name === "Villain");
    for (let i = 0; i < villain.length; i++) {
      if (numWeapon > 0) {
        // document.getElementById("Normal").pause();
        // document.getElementById("Armado").play();
        villain[i].killed(true);
      } else {
        // document.getElementById("Normal").play();
        // document.getElementById("Armado").pause();
        villain[i].killed(false);
      }
    }
    vueBus.$emit("Xterminate: set game data", { weapons: numWeapon, villains: villain.length });

    if (villain.length === 0) {
      vueBus.$emit("Xterminate: game finished", 2);
    }
  }

  /*
   * @function - sensing
   * Sensing of agents oriented programming 3d interfaces
   * @param - scene (ThreeJS Scene Instance): Scene instance of the 3d scene
   */
  function sensing() {
    const objects = scene.children.filter(el => el.name === "Wall");

    vector.set(0, 0, -1);
    raycasterHero.set(this.position, vector);
    const objectFront = raycasterHero.intersectObjects(objects);

    vector.set(0, 0, 1);
    raycasterHero.set(this.position, vector);
    const objectBack = raycasterHero.intersectObjects(objects);

    vector.set(1, 0, 0);
    raycasterHero.set(this.position, vector);
    const objectRight = raycasterHero.intersectObjects(objects);

    vector.set(-1, 0, 0);
    raycasterHero.set(this.position, vector);
    const objectLeft = raycasterHero.intersectObjects(objects);

    vector.set(Math.sin(this.rotation.y), 0, Math.cos(this.rotation.y));
    raycasterCam.set(this.position, vector);
    objectBackCam = raycasterCam.intersectObjects(objects);

    vector.set(-Math.sin(this.rotation.y), 0, -Math.cos(this.rotation.y));
    raycasterCam.set(this.position, vector);
    objectFrontCam = raycasterCam.intersectObjects(objects);

    crashF = 0;
    if (objectFront.length > 0 && objectFront[0].distance <= minDistance) {
      crashF = 1;
    }

    crashR = 0;
    if (objectRight.length > 0 && objectRight[0].distance <= minDistance) {
      crashR = 1;
    }

    crashL = 0;
    if (objectLeft.length > 0 && objectLeft[0].distance <= minDistance) {
      crashL = 1;
    }

    crashB = 0;
    if (objectBack.length > 0 && objectBack[0].distance <= minDistance) {
      crashB = 1;
    }
  }

  /*
   * @function - perform
   * Action of agents oriented programming 3d interfaces
   * @param - scene (ThreeJS Scene Instance): Scene instance of the 3d scene
   */
  function perform() {
    box.update();

    if (isDestroyed) {
      this.position.y = -0.55;
      this.rotation.x = Math.PI / 2;
      camera.position.set(this.position.x, 2, this.position.z);
    } else {
      if (keyEvents[37]) {
        // left Arrow
        this.rotation.y += rotBody; // Hero rotation

        // Leg movement
        if (!(keyEvents[40] || keyEvents[38])) {
          if (legL.rotation.x > 0.4) {
            rotMembers = 0;
          }
          legL.rotation.x += rotMembers;
          legR.rotation.x -= rotMembers;
          armL.rotation.x -= rotMembers * 1.8;
          armR.rotation.x += rotMembers * 1.8;
          rotMembers = 0.03;
        }
      }

      if (keyEvents[38]) {
        // Up Arrow
        // Hero movement
        if (
          (crashF === 1 && Math.cos(this.rotation.y) > 0) ||
          (crashB === 1 && Math.cos(this.rotation.y) <= 0)
        ) {
          this.position.z -= 0;

          if (
            (crashL === 1 && Math.sin(this.rotation.y) > 0) ||
            (crashR === 1 && Math.sin(this.rotation.y) <= 0)
          ) {
            this.position.x -= 0;
          } else {
            this.position.x -= velocity * Math.sin(this.rotation.y);
          }
        } else if (
          (crashL === 1 && Math.sin(this.rotation.y) > 0) ||
          (crashR === 1 && Math.sin(this.rotation.y) <= 0)
        ) {
          this.position.x -= 0;

          if (
            (crashF === 1 && Math.cos(this.rotation.y) > 0) ||
            (crashB === 1 && Math.cos(this.rotation.y) <= 0)
          ) {
            this.position.z -= 0;
          } else {
            this.position.z -= velocity * Math.cos(this.rotation.y);
          }
        } else {
          this.position.z -= velocity * Math.cos(this.rotation.y);
          this.position.x -= velocity * Math.sin(this.rotation.y);
        }

        // Camera movement
        if (objectBackCam.length && objectBackCam[0].distance < 4) {
          camera.position.set(
            objectBackCam[0].distance * Math.cos(this.rotation.y - Math.PI / 2) + this.position.x,
            0,
            -objectBackCam[0].distance * Math.sin(this.rotation.y - Math.PI / 2) + this.position.z
          );
        } else {
          camera.position.set(
            4 * Math.cos(this.rotation.y - Math.PI / 2) + this.position.x,
            0,
            -4 * Math.sin(this.rotation.y - Math.PI / 2) + this.position.z
          );
        }

        // Leg movement
        if (Math.abs(legL.rotation.x) > 0.4) {
          rotMembers = -rotMembers;
        }

        legL.rotation.x += rotMembers;
        legR.rotation.x -= rotMembers;
        armL.rotation.x -= rotMembers * 1.8;
        armR.rotation.x += rotMembers * 1.8;
      }

      if (keyEvents[39]) {
        // Right Arrow
        this.rotation.y -= rotBody; // Hero rotation

        // Leg movement
        if (!(keyEvents[40] || keyEvents[38])) {
          if (legR.rotation.x > 0.4) {
            rotMembers = 0;
          }
          legL.rotation.x -= rotMembers;
          legR.rotation.x += rotMembers;
          armL.rotation.x += rotMembers * 1.8;
          armR.rotation.x -= rotMembers * 1.8;
          rotMembers = 0.03;
        }
      }

      if (keyEvents[40]) {
        // Down Arrow
        // Hero movement
        if (
          (crashB === 1 && Math.cos(this.rotation.y) > 0) ||
          (crashF === 1 && Math.cos(this.rotation.y) <= 0)
        ) {
          this.position.z += 0;

          if (
            (crashR === 1 && Math.sin(this.rotation.y) > 0) ||
            (crashL === 1 && Math.sin(this.rotation.y) <= 0)
          ) {
            this.position.x += 0;
          } else {
            this.position.x += velocity * 0.7 * Math.sin(this.rotation.y);
          }
        } else if (
          (crashR === 1 && Math.sin(this.rotation.y) > 0) ||
          (crashL === 1 && Math.sin(this.rotation.y) <= 0)
        ) {
          this.position.x += 0;

          if (
            (crashB === 1 && Math.cos(this.rotation.y) > 0) ||
            (crashF === 1 && Math.cos(this.rotation.y) <= 0)
          ) {
            this.position.z += 0;
          } else {
            this.position.z += velocity * 0.7 * Math.cos(this.rotation.y);
          }
        } else {
          this.position.z += velocity * 0.7 * Math.cos(this.rotation.y);
          this.position.x += velocity * 0.7 * Math.sin(this.rotation.y);
        }

        // Camera movement
        if (objectFrontCam.length && objectFrontCam[0].distance < 4) {
          camera.position.set(
            -objectFrontCam[0].distance * Math.cos(this.rotation.y - Math.PI / 2) + this.position.x,
            0,
            objectFrontCam[0].distance * Math.sin(this.rotation.y - Math.PI / 2) + this.position.z
          );
        } else {
          camera.position.set(
            -4 * Math.cos(this.rotation.y - Math.PI / 2) + this.position.x,
            0,
            4 * Math.sin(this.rotation.y - Math.PI / 2) + this.position.z
          );
        }

        // Leg movement
        if (Math.abs(legL.rotation.x) > 0.4) {
          rotMembers = -rotMembers;
        }

        legL.rotation.x -= rotMembers * 0.7;
        legR.rotation.x += rotMembers * 0.7;
        armL.rotation.x += rotMembers * 1.8;
        armR.rotation.x -= rotMembers * 1.8;
      }

      if (!(keyEvents[40] || keyEvents[39] || keyEvents[38] || keyEvents[37])) {
        legL.rotation.x = 0;
        legR.rotation.x = 0;
        armL.rotation.x = 0;
        armR.rotation.x = 0;
      }
    }

    // Camera look to hero
    camera.lookAt(this.position);
  }

  this.sensing = sensing;
  this.perform = perform;
  this.killed = killed;
  this.damaged = damaged;
  this.charge = charge;
  this.keyboardEvent = keyboardEvent;
}
